import React, { memo, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormSpy } from 'react-final-form';
import { CheckboxCheck } from '../../../../components/form/FormCheckboxCheck';
import '../../../../sass/materialFacts.scss';
import { forwardRef } from 'react';
import { getDirtyStep, getLeaseType, getLocation, getMaterialFacts, getStep } from '../../../../selectors/lease';
import { formSubmitFail, setDirtyStep, updateSubmitTypeSuccess } from '../../../../actions/lease';
import axios from 'axios';
import * as Lease from '../../../../reducers/lease';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { has } from 'lodash';
import { MaterialFactsGroup } from '../../../../components/lease/mainScreen/common/sales/MaterialFactsGroup';

const MATERIAL_FACTS_FORM = 'materialFactsForm';

const MaterialFacts = forwardRef((props, ref) => {
    const dispatch = useDispatch();
    const location = useSelector(getLocation);
    const leaseType = useSelector(getLeaseType);
    const reduxMaterialFacts = useSelector(getMaterialFacts);
    const step = useSelector(getStep);
    const dirtyStep = useSelector(getDirtyStep);

    let [materialFacts, setMaterialFacts] = useState(reduxMaterialFacts);

    // Called from parent LeaseAgreementForm nextStep
    useImperativeHandle(ref, () => ({
        submitStep() {
            document.getElementById(MATERIAL_FACTS_FORM).dispatchEvent(
                new Event('submit', {
                    cancelable: true,
                    bubbles: true
                })
            );
        }
    }));

    const addOtherMaterialFact = push => {
        push('clauseList', {
            optionType: '',
            title: '',
            description: ''
        });
    };

    const handleFormDirtyChange = form => {
        if (form.getState().dirty) {
            if (dirtyStep !== step) {
                dispatch(setDirtyStep(step));
            }
        }
    };

    const updateMaterialFacts = data => {
        return axios.post(`/api/agency/lease/${props.leaseId}/material-facts`, data);
    };

    const submitForm = values => {
        /**
         * Store the ref of the component so it can be used in this function
         * We need this otherwise the promise below doesn't have access to the ref
         */
        const currentRef = ref.current;
        /**
         * Grab the bypassFormValidation that was set from the parent component: LeaseAgreementForm.js
         */
        values.bypassFormValidation = currentRef.bypassFormValidation;
        return updateMaterialFacts(values)
            .then(result => {
                dispatch(updateSubmitTypeSuccess(result, Lease.LEASE_MATERIAL_FACTS_SUCCESS)).then(() => {
                    /**
                     * Callback after submit this form so that the parent component can take an action
                     */
                    if (currentRef) {
                        currentRef.callbackAfterSubmit();
                    }
                });
            })
            .catch(error => {
                if (has(error, 'response.data.errors.materialFacts')) {
                    return error.response.data.errors.materialFacts;
                } else if (has(error, 'response.data.errors')) {
                    dispatch(formSubmitFail(error.response.data.errors, leaseType, location));
                }
            });
    };

    return (
        <div className="materialFacts fixed-width">
            <p className="help-text">Please details any material facts associated with the Property:</p>
            <Form
                onSubmit={submitForm}
                initialValues={materialFacts}
                mutators={{
                    ...arrayMutators
                }}
            >
                {({
                    handleSubmit,
                    values,
                    form,
                    form: {
                        mutators: { push }
                    }
                }) => {
                    return (
                        <form id={MATERIAL_FACTS_FORM} onSubmit={handleSubmit}>
                            <FormSpy subscription={{ values: true }} onChange={() => handleFormDirtyChange(form)} />
                            <FieldArray name="clauseList" initialValue={values.clauseList}>
                                {({ fields }) => (
                                    <React.Fragment>
                                        {fields.map((name, index) => {
                                            return (
                                                <MaterialFactsGroup
                                                    index={index}
                                                    key={index}
                                                    removeOtherMaterialFact={() => fields.remove(index)}
                                                    values={values}
                                                />
                                            );
                                        })}
                                    </React.Fragment>
                                )}
                            </FieldArray>
                            <div className="button">
                                <button type="button" onClick={() => addOtherMaterialFact(push)}>
                                    Add material fact
                                </button>
                            </div>
                            <CheckboxCheck
                                name="askClientToDisclose"
                                label="Ask the Client to disclose any material facts associated with the Property"
                            />
                        </form>
                    );
                }}
            </Form>
        </div>
    );
});

export default memo(MaterialFacts);
